"use client";

import Button from '@/components/ui/Button';
import { currencies } from "@/constants/currencies";
import useHotelDetailStore from '@/core/stores/hotelDetail';
import { useRoomsStore } from '@/core/stores/rooms';
import useProvider1Store from '@/core/stores/provider1';
import useNextRouter from '@/hooks/useNextRouter';
import useSetSearchParams from '@/hooks/useSetSearchParams';
import useToast from '@/hooks/useToast';
import { deCodeOccupancies } from "@/utils/deCodeOccupancies";
import getNextDate from "@/utils/getNextDay";
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Cookies from 'js-cookie';
import { useParams } from "next/navigation";
import { useState } from "react";
import RateCancellationInfo from './CancellationPolicies';
import CheckAvailability from './CheckAvailability';


type ChildType = {
  name: string;
  age: string;
  lastName?: string;
};

type AdultType = {
  name: string;
  lastName: string;
};

export interface PassengersType {
  rateKey: string;
  adults: AdultType[];
  children: ChildType[];
}

interface Occupancies {
  roomTypeId: string;
  adults: number;
  children: number;
  passengersDetails?: {
    firstName: string;
    lastName: string;
    gender: string;
  }[];
}

export default function HotelDetailsBill() {
  const { push } = useNextRouter();

  const { city } = useParams<{ city: string; hotelId: string }>();
  const [loading, setLoading] = useState(false)

  const { errorToast } = useToast()

  const { params } = useSetSearchParams();

  const { checkIn = getNextDate(0), checkOut = getNextDate(1), passenger = 'a1c-1', lat, lng } = params

  let roomsParam = "";

  const { selectedRooms, toggleSelectedRoom, hotelCode,
    setOrderStatus, setOrderId, mapRoomIds, addRoomsSpecials, hotelData } = useProvider1Store()

  const totalPrice = selectedRooms.reduce((total, { selectedRate: { price } }) => {
    return total + parseFloat(price);
  }, 0);

  const { setTotalAfterDiscount, setDiscountPrice, } = useRoomsStore();


  const passengers = passenger
    ? deCodeOccupancies(passenger)?.map((room) => ({
      adults: room.adults,
      children: room.children.length,
    }))
    : [];

  const handlePush = async () => {
    const decodePassengers = deCodeOccupancies(passenger || "a1_c-1")?.map(
      (room) => {
        const occupancy: any = {
          rooms: 1,
          adults: room.adults,
          children: room.children.length,
        };
        if (room.children.length) {
          occupancy.childrenAge = room.children;
        }
        return occupancy;
      },
    );

    if (selectedRooms.length !== decodePassengers.length) {
      errorToast(`You have searched for ${decodePassengers.length} rooms but currently selected ${selectedRooms.length}`)
      return
    }

    setLoading(true)
    const occupancies: Occupancies[] = selectedRooms
      .map((item, index) => {
        if (item.occupancies) {
          const occupyObject: any = {
            ...item.occupancies,
            adults: decodePassengers[index].adults,
            children: decodePassengers[index].children,
            rateBasisId: item.selectedRate.rateBasisId,
            allocationDetails: item.selectedRate.allocationDetails,
          };

          if (decodePassengers[index].children) {
            occupyObject.childrenAge = decodePassengers[index].childrenAge;
          }

          if ('passengersDetails' in occupyObject) {
            delete occupyObject.passengersDetails;
          }

          return occupyObject;
        } else {
          return null;
        }
      })
      .filter((occupancy): occupancy is NonNullable<typeof occupancy> => Boolean(occupancy));


    try {
      const response = await fetch('/api/orders/pre-book', {
        method: 'POST',
        body: JSON.stringify({
          code: hotelCode,
          occupancies,
          stay: {
            checkIn,
            checkOut
          }
        })
      })

      const { result } = await response.json()

      console.log('❌❌❌', result, response);


      if (response.ok && result.status === 'successful') {
        setOrderId(result.orderId)
        mapRoomIds(result.room,)
        setTotalAfterDiscount(0)
        setDiscountPrice(0)
        result.room.map((room: any) => {
          if (room.specialRequests) {
            addRoomsSpecials(room.roomId, room.specialRequests)
          }
        })
        push(`/checkout?city=${city}&orderId=${result.orderId}&hotelCode=${hotelCode}&checkIn=${checkIn || getNextDate(0)}&checkOut=${checkOut || getNextDate(14)}&hotelId=${hotelData?.id}&rooms=${roomsParam}&passenger=${passenger || "a1_c-1"}&lat=${lat}&lng=${lng}`);
      } else if (response.ok && result.status === "unsuccessful") {

        result.room.map((item: any) => {
          if (item.status === "unsuccessful") {
            setOrderStatus(item.index, "unsuccessful")
            errorToast(`${item.roomName} is not currently available.`)
          }
        })

      } else {
        errorToast(result?.message ||
          (result.error ? result.error.includes('Ensure you have retrieved') ? 'Your search results are stale! please refresh the page' : result.error : '')
          || 'Booking failed!')
      }


    } catch (err) {

      errorToast('Booking Failed')
    }
    setLoading(false)



  };
  const currency = Cookies.get('currency') || 'USD'



  return (

    <div className="sticky hidden lg:block z-[110]  right-0 top-28 mx-auto mr-0 lg:rounded-b-3xl rounded-t-3xl border bg-white shadow-card-top  lg:border-0">
      {selectedRooms.length === 0 && <CheckAvailability />}

      {selectedRooms.length > 0 && <div className=" hidden md:block p-4">
        <div className="">
          <p className="text-xs font-normal text-gray-800">You selected </p>

          <div>
            <p className=' font-bold pb-2 border-b'>{Object.keys(selectedRooms).length} {passengers.length > 0 ? `Room for ${passengers[0]?.adults + passengers[0]?.children} Guest` : ''}</p>

            <div className="flex w-full flex-col gap-2 pt-3 max-h-[48dvh] overflow-y-auto small-scroller">
              {selectedRooms.map((room, index) => (
                <button key={index + 'room-card-bill'}
                  onClick={() => toggleSelectedRoom(room, room.selectedRate.allocationDetails, room.roomIndex)}
                  className={` border p-4 rounded-3xl relative group flex flex-row items-start justify-between gap-2 `} >
                  <div className="flex w-full flex-col line-clamp-1 font-semibold text-gray-700" key={room.roomTypeId + 'room-card' + index} >
                    <p className="flex w-full items-center gap-1 justify-between">
                      <span className={`${room.orderStatus === 'unsuccessful' ? 'text-rose-500' : ''} capitalize`}>
                        {room.name.toLowerCase()}
                        {room.orderStatus === 'unsuccessful' && <span className='text-xs '> - (Not Available)</span>}
                      </span>
                    </p>
                    <div className="pt-2 mb-1 text-xs font-normal flex flex-row items-center gap-2">
                      <FontAwesomeIcon icon={faUser} className="w-4 h-4" />
                      <p>
                        <span>{room.maxOccupancy} Occupancy</span>
                      </p>
                    </div>
                    <RateCancellationInfo withDescription price={`${currencies[currency]}${room.selectedRate.price}`} rate={room.selectedRate} />
                    <button className='absolute top-3 aspect-square  rounded hover:text-white 
                                hover:bg-rose-500 transition-colors duration-200
                                  size-6 flex items-center justify-center bg-rose-100 text-rose-500 text-sm right-3'>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-2 hidden lg:block">
          {/* <p className=" font-bold text-gray-800">Price Detail</p> */}
        </div>
        <div className="flex border-t border-dashed flex-row justify-between pt-3 font-bold text-gray-900">
          <p>Total</p>
          <p>
            {currencies[currency]}
            {totalPrice === 0 ? '0.00' : totalPrice.toFixed(2)}
          </p>
        </div>
        <div className="w-full pt-4">
          <Button
            isLoading={loading}
            color='primary'
            radius='rounded-full'
            onClick={handlePush}
            disabled={selectedRooms.length === 0 || loading}
            className={`w-full min-w-full px-5 py-2.5 text-white`} >
            Book Now
          </Button>
        </div>
      </div>}
    </div>

  );
};
